import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { Card, Dropdown, ListGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import FalconCardHeader from 'components/common/FalconCardHeader';
import axiosInstance from 'axiosInstance';

const formatNotificationTime = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const SalesNotificationDropdown = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const unreadCount = useMemo(
    () => notifications.filter(item => !item.is_read).length,
    [notifications]
  );

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('sales/activities/notifications/');
      setNotifications(response.data?.notifications || []);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadNotifications();
  }, [isOpen]);

  const markAllRead = async event => {
    event.preventDefault();
    const unread = notifications.filter(item => !item.is_read);
    await Promise.all(unread.map(item => axiosInstance.post(`sales/activities/notifications/${item.id}/read/`)));
    setNotifications(previous => previous.map(item => ({ ...item, is_read: true })));
  };

  const openNotification = async item => {
    if (!item.is_read) {
      try {
        await axiosInstance.post(`sales/activities/notifications/${item.id}/read/`);
        setNotifications(previous => previous.map(notification => (
          notification.id === item.id ? { ...notification, is_read: true } : notification
        )));
      } catch {
        return;
      }
    }
    setIsOpen(false);
  };

  const previewNotifications = notifications.slice(0, 8);

  return (
    <Dropdown navbar={true} as="li" show={isOpen} onToggle={setIsOpen}>
      <Dropdown.Toggle
        bsPrefix="toggle"
        as={Link}
        to="#!"
        className={classNames('px-0 nav-link', {
          'notification-indicator notification-indicator-primary': unreadCount > 0
        })}
      >
        <FontAwesomeIcon icon="bell" transform="shrink-6" className="fs-4" />
        {unreadCount > 0 ? (
          <span className="notification-indicator-number">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </Dropdown.Toggle>

      <Dropdown.Menu className="dropdown-menu-card dropdown-menu-end dropdown-caret dropdown-caret-bg">
        <Card className="dropdown-menu-notification dropdown-menu-end shadow-none" style={{ maxWidth: '20rem' }}>
          <FalconCardHeader
            className="card-header"
            title={t('SALES.NOTIFICATIONS')}
            titleTag="h6"
            light={false}
            endEl={
              <Link className="card-link fw-normal" to="#!" onClick={markAllRead}>
                {t('NOTIFICATIONS.ACTIONS.MARK_ALL_READ')}
              </Link>
            }
          />
          <ListGroup variant="flush" className="fw-normal fs--1 scrollbar" style={{ maxHeight: '19rem' }}>
            {loading ? (
              <div className="py-4 text-center"><Spinner animation="border" size="sm" className="me-2" />{t('NOTIFICATIONS.LOADING')}</div>
            ) : previewNotifications.length ? (
              previewNotifications.map(item => (
                <ListGroup.Item key={item.id} className={classNames({ 'bg-light': !item.is_read })}>
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div className="fw-semi-bold">{item.title}</div>
                      <div className="text-600 fs--1">{item.body}</div>
                      <div className="text-500 fs--2 mt-1">{formatNotificationTime(item.created_at)}</div>
                    </div>
                    <Link to="#!" onClick={() => openNotification(item)}>{t('SALES.OPEN')}</Link>
                  </div>
                </ListGroup.Item>
              ))
            ) : (
              <div className="py-4 px-3 text-center text-600">{t('NOTIFICATIONS.EMPTY')}</div>
            )}
          </ListGroup>
          <div className="card-footer text-center border-top">
            <Link className="card-link d-block" to="/sales/dashboard" onClick={() => setIsOpen(false)}>
              {t('SALES.GO_DASHBOARD')}
            </Link>
          </div>
        </Card>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default SalesNotificationDropdown;
